import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import {
  useParams,
  useNavigate,
  useLocation,
  useSearchParams,
  Link,
} from "react-router-dom";
import { ArtData, SITE_DATA_PARTITIONS } from "../../../../../types";
import { Input } from "../../../components/forms";
import { Layout } from "../../../components/Layout";
import { Button, BUTTON_COLORS, BUTTON_TYPE } from "../../../components/Button";
import { usePermissions } from "../../../providers/authProvider";
import { OrderData } from "../../../../../types/order";
import { Api } from "../../../api";
import {
  ShipmentData,
  ShipmentKeyComponents,
  SHIPPING_PROVIDERS,
} from "../../../../../types/shipments";
import generators from "../../../../../shared-utils/noDep/generators";
import { ArtList } from "../../../components/ArtList";
import { useShipment } from "../../../hooks/useShipment";
import { useOrder } from "../../../hooks/useOrder";
import { OrderInfo } from "../../../components/order";
import {
  NEW_SLUG,
  ORDER_LIST_URL,
  ORDER_URL,
  USER_AUTH_URL,
} from "../../../config";

export const UpdateShipment = ({
  slug,
  shipment,
  order,
  admin,
  unclaimedItems,
}: {
  slug: string;
  order: OrderData;
  shipment?: ShipmentData;
  admin: boolean;
  unclaimedItems: ArtData[];
}) => {
  const navigate = useNavigate();

  const shipmentRef = useRef(
    shipment ??
      ({
        type: SITE_DATA_PARTITIONS.SHIPMENT,
        slug: generators.randomNumberString(12),
        orderSlug: order.slug,
        items: unclaimedItems ?? [],
        isShipped: false,
        shippingProvider: {
          type: SHIPPING_PROVIDERS.NONE,
        },
      } as ShipmentData),
  );
  const [currShipment, setCurrShipment] = useState(shipmentRef.current);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const inSync = Object.keys({ ...currShipment, ...shipmentRef.current }).every(
    (key) =>
      JSON.stringify(currShipment[key as keyof typeof currShipment]) ===
      JSON.stringify(shipmentRef.current[key as keyof typeof currShipment]),
  );
  const updateShipment = useCallback(
    (updatedShipment: ShipmentData & ShipmentKeyComponents) => {
      if (updatedShipment.isShipped && !updatedShipment.shippingProvider.type) {
        setErrors((e) => [
          ...e,
          "Please provide shipping provider if shipped.",
        ]);
        return;
      }

      if (
        updatedShipment.shippingProvider.type === SHIPPING_PROVIDERS.OTHER &&
        (!updatedShipment.shippingProvider.name ||
          !updatedShipment.shippingProvider.contactInfo)
      ) {
        setErrors((e) => [
          ...e,
          "Please provide name and contact info for other shipper",
        ]);
        return;
      }

      if (
        updatedShipment.isShipped &&
        updatedShipment.shippingProvider.type &&
        updatedShipment.shippingProvider.type !== SHIPPING_PROVIDERS.OTHER &&
        !updatedShipment.shippingProvider.trackingNumber
      ) {
        setErrors((e) => [...e, "Please provide tracking number if shipped"]);
        return;
      }
      setLoading(true);

      const manipulation =
        slug === NEW_SLUG ? Api.create<ShipmentData> : Api.update<ShipmentData>;
      manipulation({ ...updatedShipment })
        .then((res) => {
          if (res.success) {
            setLoading(false);
            shipmentRef.current = {
              ...shipmentRef.current,
              ...updatedShipment,
            };
            navigate(ORDER_URL(order.slug, admin));
          } else {
            throw new Error(res.error);
          }
        })
        .catch((e) => {
          setLoading(false);
          setErrors((errors) => [...errors, e.message]);
        });
    },
    [slug, setLoading, order.slug, admin],
  );

  const deleteShipment = useCallback(() => {
    setLoading(true);
    const manipulation = Api.delete;
    manipulation({ type: currShipment.type, slug: currShipment.slug })
      .then((res) => {
        setLoading(false);
        if (res.success) {
          setTimeout(() => navigate(ORDER_URL(order.slug, admin)), 200);
        } else {
          setErrors((e) => [...e, res.error ?? "Failed to delete shipment"]);
        }
      })
      .catch((e) => setLoading(false));
  }, [slug, setLoading, currShipment.slug, currShipment.type]);

  const handleChange = (
    evt: ChangeEvent<HTMLInputElement & HTMLSelectElement>,
  ) => {
    const { value, name } = evt.target;
    const [root, subKey] = name.split(".");

    setCurrShipment((shipment) => {
      return {
        ...shipment,
        [root]:
          root === "slug"
            ? value.replace(/\s/, "").toLowerCase()
            : root === "shippingProvider"
              ? { ...shipment[root], [subKey]: value }
              : value,
      };
    });
  };
  const height = 200;
  const hoverHeight = 250;
  return (
    <>
      <div className="min-h-[20px] z-[1000] sticky top-0 flex justify-between gap-2 bg-main p-2 px-7 shadow-md">
        <Link
          to={ORDER_LIST_URL(undefined, admin)}
          className={BUTTON_TYPE.small + BUTTON_COLORS.secondary + ""}
        >
          Back to the order list
        </Link>

        <h1 className="text-center self-center">
          {slug === NEW_SLUG
            ? "Create a shipment"
            : "Editing shipment #" + currShipment.slug}
        </h1>
        {loading ? (
          <div className={BUTTON_TYPE.small + BUTTON_COLORS.warning}>
            Loading
          </div>
        ) : inSync && slug !== NEW_SLUG ? (
          <div className={BUTTON_TYPE.small + BUTTON_COLORS.success}>
            In sync
          </div>
        ) : (
          <button
            className={BUTTON_TYPE.small + BUTTON_COLORS.warning}
            onClick={() => updateShipment(currShipment)}
          >
            {slug === NEW_SLUG ? "Create" : "Sync now"}
          </button>
        )}
      </div>
      <div className="px-10 pb-16 flex-1 ">
        <div className="min-h-[20px] z-[1000] fixed top-4 right-4">
          {errors.map((e, idx) => (
            <div
              className="border-2 border-textWarning bg-white p-2 rounded-xl my-2 flex justify-around"
              key={e}
            >
              <div className="flex-1 basis-9/12 m-1">{e}</div>
              <div className="flex-1 basis-1/12 align-center">
                <Button
                  btnType="nav"
                  btnColor="warning"
                  className="w-[64px] h-[64px] text-3xl !p-0"
                  onClick={() => {
                    setErrors((errors) => errors.filter((_e, i) => idx != i));
                  }}
                >
                  🗑️
                </Button>
              </div>
            </div>
          ))}
        </div>
        <Input
          label="Slug"
          name="slug"
          disabled={slug !== NEW_SLUG || loading}
          placeholder="Slug"
          value={currShipment.slug}
          onChange={handleChange}
        />
        <Input
          name="shippingProvider.type"
          placeholder="USPS"
          disabled={loading}
          value={currShipment.shippingProvider.type}
          onChange={handleChange}
          inputType="select"
          label="Shipping provider"
        >
          <option value={SHIPPING_PROVIDERS.NONE}>Select</option>
          <option value={SHIPPING_PROVIDERS.FEDEX}>FedEx</option>
          <option value={SHIPPING_PROVIDERS.USPS}>USPS</option>
          <option value={SHIPPING_PROVIDERS.UPS}>UPS</option>
          <option value={SHIPPING_PROVIDERS.OTHER}>OTHER</option>
        </Input>
        {currShipment.shippingProvider.type === SHIPPING_PROVIDERS.OTHER ? (
          <>
            <Input
              label="Name"
              name="shippingProvider.name"
              disabled={loading}
              placeholder="Fred's local deliveries"
              value={currShipment.shippingProvider.name}
              onChange={handleChange}
            />

            <Input
              label="Contact info"
              name="shippingProvider.contactInfo"
              disabled={loading}
              placeholder="417 113 1400"
              value={currShipment.shippingProvider.contactInfo}
              onChange={handleChange}
            />
          </>
        ) : currShipment.shippingProvider.type ===
          SHIPPING_PROVIDERS.NONE ? null : (
          <Input
            label="Tracking number"
            name="shippingProvider.trackingNumber"
            disabled={loading}
            placeholder="239461859279511"
            value={currShipment.shippingProvider.trackingNumber}
            onChange={handleChange}
          />
        )}
        <div>
          <input
            name="isShipped"
            id="isShipped"
            type="checkbox"
            className=""
            disabled={loading}
            checked={currShipment.isShipped ? true : false}
            onChange={(evt) => {
              const { checked, name } = evt.target;
              setCurrShipment((shipment) => ({
                ...shipment,
                [name]: checked,
              }));
            }}
          />
          <label htmlFor="isShipped" className="m-2 cursor-pointer">
            Is shipped
          </label>
        </div>

        {slug !== NEW_SLUG ? (
          <Button btnColor="warning" btnType="small" onClick={deleteShipment}>
            DELETE
          </Button>
        ) : null}
      </div>
      <div className=" flex flex-col 2xl:flex-row flex-wrap">
        <div className=" flex-1   border-2 border-primary">
          <h2 className="p-2">Items in shipment</h2>
          <ArtList
            items={currShipment.items}
            noArt={<>No art in shipment! (add below)</>}
            getArtConfig={(art) => ({
              href: "",
              height: 100,

              actionBelow: {
                actionComponent: (
                  <Button
                    btnType="xs"
                    btnColor="warning"
                    onClick={() =>
                      setCurrShipment((curr) => ({
                        ...curr,
                        items: curr.items.filter(
                          (currItem) => currItem.slug !== art.slug,
                        ),
                      }))
                    }
                  >
                    Ship in another batch
                  </Button>
                ),
              },
            })}
          />
        </div>

        <div className=" flex-1   border-2 border-primary">
          <h2 className="p-2">Items without shipment</h2>
          <ArtList
            items={unclaimedItems.filter(
              (item) =>
                !currShipment.items.find(
                  (shipItem) => shipItem.slug === item.slug,
                ),
            )}
            noArt={<>No art left to be shipped</>}
            getArtConfig={(art) => ({
              href: "",
              height: 200,
              actionBelow: {
                actionComponent: (
                  <Button
                    btnType="xs"
                    btnColor="primary"
                    onClick={() =>
                      setCurrShipment((curr) => ({
                        ...curr,
                        items: [...curr.items, art],
                      }))
                    }
                  >
                    Add to shipment
                  </Button>
                ),
              },
            })}
          />
        </div>
      </div>
    </>
  );
};

const AdminShipmentPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const orderSlug = searchParams.get("orderSlug");
  const navigate = useNavigate();
  const location = useLocation();
  const permissions = usePermissions();

  useEffect(() => {
    if (!permissions?.data?.loggedIn) {
      navigate(
        USER_AUTH_URL("login", { flow: "resume", to: location.pathname }),
      );
      return;
    }
    if (!permissions?.data?.user?.admin) {
      navigate("/");
      return;
    }
  }, [permissions, navigate, location.pathname]);
  const [shipmentRequest] = useShipment({
    slug: !orderSlug ? slug : undefined,
    permissions,
  });

  const [orderRequest] = useOrder({
    slug: orderSlug ?? shipmentRequest.shipment?.orderSlug,
    permissions,
  });

  return (
    <Layout headerStyle="sm">
      <div className="p-4 pb-10">
        {orderRequest.status === "present" &&
        orderRequest.order &&
        orderRequest.shipmentsRequest?.status === "present" &&
        orderRequest.shipmentsRequest.shipments &&
        (slug === NEW_SLUG ||
          orderRequest.shipmentsRequest.shipments.find(
            (ship) => ship.slug === slug,
          )) ? (
          <UpdateShipment
            order={orderRequest.order}
            slug={slug}
            shipment={orderRequest.shipmentsRequest.shipments.find(
              (ship) => ship.slug === slug,
            )}
            unclaimedItems={orderRequest.order.items}
            admin={true}
          />
        ) : (
          <>
            {slug !== NEW_SLUG ? (
              <div>
                Could not find original shipment in order, try refreshing
              </div>
            ) : null}
            <div>{orderRequest.shipmentsRequest?.status}</div>
            <div>
              {JSON.stringify(orderRequest.shipmentsRequest?.shipments)}
            </div>
            <div>{orderRequest.shipmentsRequest?.error}</div>
          </>
        )}
        Order Info
        <OrderInfo
          {...orderRequest}
          showViewShipmentBtn
          showViewOrderBtn
          showShipmentItems
          showOriginalItems
          admin={true}
        />
      </div>
    </Layout>
  );
};

export default AdminShipmentPage;
